import { useState, useEffect, useCallback } from 'react';

type Theme = 'dark' | 'light' | 'system';

function getSystemTheme(): 'dark' | 'light' {
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

/* --surface, the colour of the top nav, in each theme. An installed app paints
   its status bar with theme-color, so matching --surface is what makes the OS
   chrome meet the nav without a seam. */
const SURFACE = { dark: '#0e1016', light: '#ffffff' } as const;

/**
 * index.html ships a PAIR of theme-color metas keyed on prefers-color-scheme,
 * which is right while the theme is 'system'. Once the operator picks a theme
 * explicitly the OS preference no longer describes what is on screen, so both
 * entries are pointed at the chosen colour — whichever one the phone matches
 * then gives the same, correct answer. Going back to 'system' restores the pair.
 */
function applyThemeColor(theme: Theme) {
  const metas = document.querySelectorAll<HTMLMetaElement>('meta[name="theme-color"][media]');
  metas.forEach((m) => {
    const own = m.media.includes('dark') ? 'dark' : 'light';
    m.content = SURFACE[theme === 'system' ? own : theme];
  });
}

function applyTheme(theme: Theme) {
  const resolved = theme === 'system' ? getSystemTheme() : theme;
  document.documentElement.classList.toggle('dark', resolved === 'dark');
  applyThemeColor(theme);
}

export function useTheme() {
  const [theme, setThemeState] = useState<Theme>(() => {
    return (localStorage.getItem('jam_theme') as Theme) ?? 'dark';
  });

  const setTheme = useCallback((t: Theme) => {
    localStorage.setItem('jam_theme', t);
    setThemeState(t);
    applyTheme(t);
  }, []);

  useEffect(() => {
    applyTheme(theme);
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = () => {
      if (theme === 'system') applyTheme('system');
    };
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [theme]);

  return { theme, setTheme };
}
